import { apiClient, getApiBaseUrl } from '../config/api';

// FEAT-044 admin client (MVP-8.10a).
// Admins manage per-zip safety contacts: list, JSON bulk upsert, CSV bulk
// upsert, delete. The list endpoint is currently exposed under
// GET /api/admin/safety-contacts and returns rows sorted by zip ascending.

export interface SafetyContactRow {
  zip: string;
  nonEmergencyPolice: string;
  aps: string;
  regionalNotes: string | null;
  updatedAt: string;
}

export interface SafetyContactInput {
  zip: string;
  nonEmergencyPolice: string;
  aps: string;
  regionalNotes?: string;
}

class SafetyContactsAdminService {
  async list(): Promise<SafetyContactRow[]> {
    const response = await apiClient.get<{ contacts: SafetyContactRow[] }>(
      '/admin/safety-contacts'
    );
    return response.data.contacts ?? [];
  }

  async bulkUpsert(contacts: SafetyContactInput[]): Promise<SafetyContactRow[]> {
    const response = await apiClient.post<{
      upsertedCount: number;
      contacts: SafetyContactRow[];
    }>('/admin/safety-contacts', { contacts });
    return response.data.contacts ?? [];
  }

  async uploadCsv(csvText: string): Promise<SafetyContactRow[]> {
    const baseUrl = await getApiBaseUrl();
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${baseUrl}/admin/safety-contacts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/csv',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: csvText,
    });
    const data = await response.json();
    if (!response.ok) {
      const message =
        (data?.error?.message as string | undefined) ?? `CSV import failed: ${response.status}`;
      throw new Error(message);
    }
    return (data?.contacts ?? []) as SafetyContactRow[];
  }

  async remove(zip: string): Promise<boolean> {
    const response = await apiClient.delete<{ zip: string; deleted: boolean }>(
      `/admin/safety-contacts/${encodeURIComponent(zip)}`
    );
    return Boolean(response.data?.deleted);
  }
}

export const safetyContactsAdminService = new SafetyContactsAdminService();
